import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const MotivationalSection = () => {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';

  const [currentQuote, setCurrentQuote] = useState(0);
  const [inspirations, setInspirations] = useState([]);
  const [loadingInspirations, setLoadingInspirations] = useState(true);

  // Admin edit state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ text: '', reference: '', author: '', theme: '' });
  const [saving, setSaving] = useState(false);
  const [adminError, setAdminError] = useState('');

  const dailyEncouragements = [
    {
      id: 1,
      title: "Start Small, Stay Consistent",
      message: "Even memorizing one verse a week builds a foundation of faith that lasts a lifetime.",
      icon: "Seedling",
      color: "var(--color-success)"
    },
    {
      id: 2,
      title: "Every Word Matters",
      message: "Each verse you memorize becomes a source of strength, wisdom, and comfort in times of need.",
      icon: "Heart",
      color: "var(--color-primary)"
    },
    {
      id: 3,
      title: "Building Spiritual Muscle",
      message: "Like physical exercise, regular Scripture memorization strengthens your spiritual life.",
      icon: "Zap",
      color: "var(--color-accent)"
    }
  ];

  const [currentEncouragement] = useState(
    dailyEncouragements?.[Math.floor(Math.random() * dailyEncouragements?.length)]
  );

  useEffect(() => {
    loadInspirations();
  }, []);

  useEffect(() => {
    if (inspirations?.length === 0) return;
    const timer = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % inspirations?.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [inspirations?.length]);

  const loadInspirations = async () => {
    try {
      setLoadingInspirations(true);
      const { data, error } = await supabase
        ?.from('daily_inspirations')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true });

      if (!error && data?.length > 0) {
        setInspirations(data);
      } else {
        // Fallback defaults
        setInspirations([
          { id: 1, text: "Thy word have I hid in mine heart, that I might not sin against thee.", reference: "Psalm 119:11", author: "King David", theme: "Fortified Mind" },
          { id: 2, text: "Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth.", reference: "2 Timothy 2:15", author: "Apostle Paul", theme: "Bible Study" },
          { id: 3, text: "So then faith cometh by hearing, and hearing by the word of God.", reference: "Romans 10:17", author: "Apostle Paul", theme: "Faith Building" },
          { id: 4, text: "All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness.", reference: "2 Timothy 3:16", author: "Apostle Paul", theme: "Scripture Value" }
        ]);
      }
    } catch (err) {
      console.error('Error loading inspirations:', err);
    } finally {
      setLoadingInspirations(false);
    }
  };

  const nextQuote = () => setCurrentQuote((prev) => (prev + 1) % inspirations?.length);
  const prevQuote = () => setCurrentQuote((prev) => (prev - 1 + inspirations?.length) % inspirations?.length);

  const quote = inspirations?.[currentQuote];

  // Admin: open edit form for existing item
  const handleEdit = (item) => {
    setEditingItem(item?.id);
    setEditForm({ text: item?.text || '', reference: item?.reference || '', author: item?.author || '', theme: item?.theme || '' });
    setAdminError('');
  };

  // Admin: open form for new item
  const handleAddNew = () => {
    setEditingItem('new');
    setEditForm({ text: '', reference: '', author: '', theme: '' });
    setAdminError('');
  };

  const handleSave = async () => {
    if (!editForm?.text?.trim() || !editForm?.reference?.trim()) {
      setAdminError('Text and reference are required.');
      return;
    }
    setSaving(true);
    setAdminError('');
    try {
      if (editingItem === 'new') {
        const { error } = await supabase?.from('daily_inspirations')?.insert({
          text: editForm?.text?.trim(),
          reference: editForm?.reference?.trim(),
          author: editForm?.author?.trim() || null,
          theme: editForm?.theme?.trim() || null,
          display_order: (inspirations?.length || 0) + 1
        });
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('daily_inspirations')?.update({
          text: editForm?.text?.trim(),
          reference: editForm?.reference?.trim(),
          author: editForm?.author?.trim() || null,
          theme: editForm?.theme?.trim() || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', editingItem);
        if (error) throw error;
      }
      setEditingItem(null);
      await loadInspirations();
    } catch (err) {
      setAdminError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window?.confirm('Delete this inspiration?')) return;
    try {
      await supabase?.from('daily_inspirations')?.update({ is_active: false })?.eq('id', id);
      await loadInspirations();
      if (currentQuote >= inspirations?.length - 1) setCurrentQuote(0);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setAdminError('');
  };

  return (
    <div className="space-y-6">
      {/* Daily Encouragement */}
      <div className="bg-card rounded-xl p-6 shadow-warm border border-border">
        <div className="flex items-start space-x-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg"
               style={{ backgroundColor: `${currentEncouragement?.color}20` }}>
            <Icon name={currentEncouragement?.icon} size={24} color={currentEncouragement?.color} />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-heading font-semibold text-foreground mb-2">
              {currentEncouragement?.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {currentEncouragement?.message}
            </p>
          </div>
        </div>
      </div>

      {/* Scripture Quote Carousel */}
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Quote" size={18} color="var(--color-primary)" />
            <h3 className="text-base font-heading font-semibold text-foreground">
              Daily Inspiration
            </h3>
          </div>
          <div className="flex items-center space-x-1">
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                title="Manage inspirations"
                className="h-8 w-8 text-primary hover:bg-primary/10"
              >
                <Icon name="Settings" size={16} />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={prevQuote} className="h-8 w-8">
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <Button variant="ghost" size="icon" onClick={nextQuote} className="h-8 w-8">
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>

        {loadingInspirations ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <blockquote className="text-foreground leading-relaxed italic">
              "{quote?.text}"
            </blockquote>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Book" size={14} color="var(--color-primary)" />
                <cite className="text-sm font-caption font-semibold text-primary not-italic">
                  {quote?.reference}
                </cite>
              </div>
              {quote?.theme && (
                <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-md font-caption">
                  {quote?.theme}
                </span>
              )}
            </div>
            {/* Navigation Dots */}
            <div className="flex items-center justify-center space-x-2 pt-2">
              {inspirations?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuote(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentQuote
                      ? 'bg-primary w-6' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div className="bg-card rounded-xl p-6 border border-primary/30 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="Settings" size={16} color="var(--color-primary)" />
              Manage Daily Inspirations
            </h3>
            <Button variant="ghost" size="sm" onClick={handleAddNew} iconName="Plus" iconPosition="left">
              Add New
            </Button>
          </div>

          {/* Edit / Add Form */}
          {editingItem && (
            <div className="mb-4 p-4 bg-background/60 rounded-lg border border-border space-y-3">
              <h4 className="text-sm font-semibold text-foreground">
                {editingItem === 'new' ? 'Add New Inspiration' : 'Edit Inspiration'}
              </h4>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Scripture Text *</label>
                <textarea
                  className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  rows={3}
                  value={editForm?.text}
                  onChange={(e) => setEditForm(f => ({ ...f, text: e?.target?.value }))}
                  placeholder="Enter scripture text..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Reference *</label>
                  <input
                    type="text"
                    className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    value={editForm?.reference}
                    onChange={(e) => setEditForm(f => ({ ...f, reference: e?.target?.value }))}
                    placeholder="e.g. John 3:16"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Author</label>
                  <input
                    type="text"
                    className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    value={editForm?.author}
                    onChange={(e) => setEditForm(f => ({ ...f, author: e?.target?.value }))}
                    placeholder="e.g. Apostle Paul"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Theme</label>
                <input
                  type="text"
                  className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  value={editForm?.theme}
                  onChange={(e) => setEditForm(f => ({ ...f, theme: e?.target?.value }))}
                  placeholder="e.g. Faith Building"
                />
              </div>
              {adminError && (
                <p className="text-xs text-destructive">{adminError}</p>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* List of inspirations */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {inspirations?.map((item) => (
              <div
                key={item?.id}
                className="flex items-start justify-between gap-3 p-3 bg-background/50 rounded-lg border border-border/50 hover:border-primary/20 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground line-clamp-2 italic">"{item?.text}"</p>
                  <p className="text-xs text-primary mt-1 font-semibold">{item?.reference}</p>
                  {item?.theme && (
                    <span className="text-xs text-muted-foreground">{item?.theme}</span>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                    title="Edit"
                  >
                    <Icon name="Pencil" size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(item?.id)}
                    className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MotivationalSection;